import { sendAIMessage } from "@/api/aiChat";
import { useSetting } from "@/composables/use-setting";
import { useUserStore } from "@/states/user";
import { getCommonInfo } from "@voghion/native-api";
import {
  EventSourceMessage,
  EventSourceParserStream,
} from "eventsource-parser/stream";

export type MultimediaType =
  | "GREETING"
  | "GREETING_WORLD_LIST"
  | "TEXT"
  | "IMAGE"
  | "VOICE";

export type EventName =
  | "GREETING"
  | "GREETING_WORLD_LIST"
  | "TEXT"
  | "ITEM_CARD_LIST"
  | "VOICE_EXTRACTION_SUCCESS"
  | "VOICE_EXTRACTION_FAILURE";

export interface readSSEResponseOptions<T extends MultimediaType> {
  reader: ReadableStreamDefaultReader<EventSourceMessage>;
  type: T;
  onData: (message: {
    completeStatus: number;
    reasonContent: T extends "GREETING_WORLD_LIST" ? string[] : string;
    thoughtContent?: string;
    requestId?: number;
    sessionId?: number;
    stageId?: number;
  }) => void;
  onGoodsList?: (reasonContent: any) => void;
  onDone?: () => void;
  onError?: () => void;
}
export const readSSEResponse = async <T extends MultimediaType>(
  options: readSSEResponseOptions<T>,
) => {
  while (true) {
    const x = await options.reader.read();
    console.log("reader", x);
    if (x) {
      const { done, value } = x;
      const event = value?.event as EventName;
      try {
        if (value?.data) {
          const message = JSON.parse(value.data);
          if (event === "ITEM_CARD_LIST") {
            options.onGoodsList?.(message.reasonContent);
          } else if (event === "VOICE_EXTRACTION_FAILURE") {
            options.onError?.();
            break;
          } else {
            options.onData(message);
          }
        }
      } catch (e) {
        console.warn(e);
      }
      if (done) {
        options.onDone?.();
        break;
      }
    }
  }
};

export const sendMessageRequestWithSessionId = async (
  params: {
    multimediaType: string;
    content?: string;
    multimediaUrl?: string;
  },
  sessionId: number,
) => {
  const { getClientInfo } = useSetting();
  const userStore = useUserStore();
  const checkLogin = userStore.checkLogin;
  await checkLogin();
  const clientInfo = getClientInfo();
  const commonInfo = await getCommonInfo();
  if (!commonInfo.data?.userId) return;
  if (!sessionId) return;

  const res = await sendAIMessage({
    userId: commonInfo.data.userId,
    deviceCode: clientInfo.deviceCode,
    clientInfoToken: clientInfo.userToken,
    countryCode: clientInfo.country,
    language: clientInfo.language,
    sessionId: sessionId,
    clientInfo: JSON.stringify(clientInfo),
    ...params,
  });
  const data = res.body;

  if (!data) return;

  let decodedStream;

  // ✅ 判断环境是否支持 TextDecoderStream（新浏览器走快通道）
  if (window.TextDecoderStream) {
    decodedStream = data.pipeThrough(new TextDecoderStream());
  } else {
    // ✅ 用传统方式处理流
    const reader = data.getReader();
    const decoder = new TextDecoder();

    decodedStream = new ReadableStream({
      async start(controller) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const text = decoder.decode(value, { stream: true });
          controller.enqueue(text);
        }
        controller.close();
      },
    });
  }

  // ✅ 后续接 EventSourceParserStream 不变
  const reader = decodedStream
    .pipeThrough(new EventSourceParserStream())
    .getReader();

  return reader;
};
